import type { ReactNode } from "react";

import { Avatar } from "../../core/Avatar.js";
import { Badge } from "../../core/Badge.js";
import { cn, tv } from "../../foundations/cn.js";
import { FOCUS_RING } from "../../foundations/interaction.js";
import type { BattleSeatView } from "./battleTypes.js";

const AVATAR_SIZE = { xs: "xs", sm: "sm", md: "md" } as const;

const seatStyles = tv({
  base: "inline-flex min-w-0 items-center gap-1.5",
  variants: {
    layout: {
      /** Avatar beside the name: a seat header, a lobby row. */
      inline: "flex-row font-body text-text-primary",
      /** Avatar over the name: a lobby card's seats. */
      stacked: "max-w-20 flex-col gap-1 text-center font-body text-text-primary",
      /** The avatar alone: a countdown, a compact reel header. The label is its name. */
      avatar: "font-body",
      /** The name alone: the winner in a headline, in the headline's type. */
      name: "align-baseline",
    },
  },
  defaultVariants: { layout: "inline" },
});

/** An empty seat: a dashed ring the size of an avatar. */
const EMPTY_RING = {
  xs: "size-5",
  sm: "size-7",
  md: "size-10",
} as const;

export interface BattleSeatProps {
  seat: BattleSeatView;
  layout?: "inline" | "stacked" | "avatar" | "name";
  size?: keyof typeof AVATAR_SIZE;
  /** A running total or the like, after the name. */
  trailing?: ReactNode;
  /** The winner's mark; tenant copy. */
  winnerLabel?: string;
  className?: string;
}

/**
 * One battle seat: its avatar (the viewer's own in the accent, an empty seat a dashed ring) and
 * what it is called. A player's seat is a button that opens their profile, avatar and name alike;
 * a bot's or an empty seat stays plain, since there is no one to show.
 */
export function BattleSeat({
  seat,
  layout = "inline",
  size = "sm",
  trailing,
  winnerLabel = "Winner",
  className,
}: BattleSeatProps) {
  const avatar =
    seat.kind === "empty" ? (
      <span
        aria-hidden
        className={cn(
          "inline-block shrink-0 rounded-pill border border-border-strong border-dashed",
          EMPTY_RING[size],
        )}
      />
    ) : (
      <Avatar
        name={seat.label}
        size={AVATAR_SIZE[size]}
        decorative
        className={cn(
          seat.isViewer && "bg-accent-subtle text-accent-text",
          seat.kind === "bot" && "text-text-tertiary",
        )}
      />
    );
  const name = (
    <span
      className={cn(
        "min-w-0 truncate",
        layout === "stacked"
          ? "w-full text-body-b3"
          : layout === "name"
            ? ""
            : "text-body-b2-strong",
        seat.kind === "empty" ? "text-text-tertiary" : layout === "name" ? "" : "text-text-primary",
      )}
    >
      {seat.label}
    </span>
  );
  const body = (
    <>
      {layout !== "name" && avatar}
      {layout !== "avatar" && name}
    </>
  );
  // The avatar alone still names the seat: as the button's label, or as text for a reader.
  const spoken = layout === "avatar" && !seat.profile && (
    <span className="sr-only">{seat.label}</span>
  );
  return (
    <span className={seatStyles({ layout, className })}>
      {seat.profile ? (
        <button
          type="button"
          // An avatar alone is named by the label; a seat that shows its name is named by that name
          // first, so what a reader sees is what a voice user says (WCAG 2.5.3), then the action.
          aria-label={
            layout === "avatar"
              ? seat.profile.label
              : `${seat.label}, ${seat.profile.actionLabel ?? "view profile"}`
          }
          onClick={seat.profile.onShow}
          className={cn(
            "inline-flex min-w-0 cursor-pointer items-center rounded-sm text-start hover:text-accent-text",
            layout === "stacked" ? "flex-col gap-1" : "gap-1.5",
            FOCUS_RING,
          )}
        >
          {body}
        </button>
      ) : (
        <span
          className={cn(
            "inline-flex min-w-0 items-center",
            layout === "stacked" ? "flex-col gap-1" : "gap-1.5",
          )}
        >
          {body}
          {spoken}
        </span>
      )}
      {trailing}
      {seat.isWinner && layout !== "avatar" && <Badge tone="warning">{winnerLabel}</Badge>}
    </span>
  );
}
